<template>
  <DeckControlsContainer data-testid="navigation-control">
    <DeckControlButton
      class="p-3"
      data-testid="navigation-control-previous"
      :disabled="currentSlideIndex === firstValidSlide"
      @click="handlePreviousSlide"
    >
      <ArrowLeftIcon class="w-6 h-6" font-medium />
    </DeckControlButton>
    <DeckControlButton
      class="p-3"
      data-testid="navigation-control-next"
      :disabled="currentSlideIndex === lastValidSlide"
      @click="handleNextSlide"
    >
      <ArrowRightIcon class="w-6 h-6 font-medium" />
    </DeckControlButton>
  </DeckControlsContainer>
</template>

<script lang="ts" setup>
import { ArrowLeftIcon, ArrowRightIcon } from '@heroicons/vue/24/outline'
import type { Deck, DeckUserType, IncludeCompany } from '@/entities/deck'
import type { DeckWithEmbed } from '@/entities/embed'
import type { DeepReadonly } from 'vue'

const props = defineProps({
  deck: {
    type: Object as PropType<DeepReadonly<Deck & IncludeCompany>>,
    required: true,
  },
  currentSlideIndex: {
    type: Number,
    required: true,
  },
  firstValidSlide: {
    type: Number,
    required: true,
  },
  lastValidSlide: {
    type: Number,
    required: true,
  },
  analyticsEventName: {
    type: String,
    required: true,
  },
  deckUserType: {
    type: String as PropType<DeckUserType>,
    required: true,
  },
  embedDeck: {
    type: Object as PropType<DeepReadonly<DeckWithEmbed>>,
    default: null,
  },
})

const emit = defineEmits(['toggle-sidebar', 'navigation-previous-slide', 'navigation-next-slide'])
const { userRole } = useUser()
const { $analytics } = useNuxtApp()

function handlePreviousSlide() {
  $analytics.track(`${props.analyticsEventName}FloatingPreviousButtonPressed`, {
    slideId: props.deck.slides[props.currentSlideIndex].id,
    slideIndex: props.currentSlideIndex,
    deckId: props.deck.id,
    deckName: props.deck.title,
    companyId: props.deck.company.id,
    companyName: props.deck.company.name,
    isHost: props.deckUserType === 'host',
    userRole: userRole.value,
    partnerId: props.embedDeck?.partner_id,
    partnerName: props.embedDeck?.partner_name,
    embedLocation: props.embedDeck?.embed_name,
  })

  emit('navigation-previous-slide')
}

function handleNextSlide() {
  $analytics.track(`${props.analyticsEventName}FloatingNextButtonPressed`, {
    slideId: props.deck.slides[props.currentSlideIndex].id,
    slideIndex: props.currentSlideIndex,
    deckId: props.deck.id,
    deckName: props.deck.title,
    companyId: props.deck.company.id,
    companyName: props.deck.company.name,
    isHost: props.deckUserType === 'host',
    userRole: userRole.value,
    partnerId: props.embedDeck?.partner_id,
    partnerName: props.embedDeck?.partner_name,
    embedLocation: props.embedDeck?.embed_name,
  })
  emit('navigation-next-slide')
}

function _handleNavigationKeys(e: KeyboardEvent) {
  if (e.keyCode === 37) {
    $analytics.track(`${props.analyticsEventName}KeyboardPreviousButtonPressed`, {
      slideId: props.deck.slides[props.currentSlideIndex].id,
      slideIndex: props.currentSlideIndex,
      deckId: props.deck.id,
      deckName: props.deck.title,
      companyId: props.deck.company.id,
      companyName: props.deck.company.name,
      partnerId: props.embedDeck?.partner_id,
      partnerName: props.embedDeck?.partner_name,
      embedLocation: props.embedDeck?.embed_name,
    })

    emit('navigation-previous-slide')
    return
  }

  if (e.keyCode === 39) {
    $analytics.track(`${props.analyticsEventName}KeyboardNextButtonPressed`, {
      slideId: props.deck.slides[props.currentSlideIndex].id,
      slideIndex: props.currentSlideIndex,
      deckId: props.deck.id,
      deckName: props.deck.title,
      companyId: props.deck.company.id,
      companyName: props.deck.company.name,
      partnerId: props.embedDeck?.partner_id,
      partnerName: props.embedDeck?.partner_name,
      embedLocation: props.embedDeck?.embed_name,
    })

    emit('navigation-next-slide')
    return
  }
}

onMounted(() => {
  window.addEventListener('keydown', _handleNavigationKeys)
})

onUnmounted(() => {
  window.removeEventListener('keydown', _handleNavigationKeys)
})
</script>
